import axios from 'axios';
import { webOrigin } from '@/lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Stable device id (localStorage) — used for credit balance + guardian circle
export function getDeviceId() {
  let id = localStorage.getItem('unmanip_device_id');
  if (!id) { id = crypto.randomUUID(); localStorage.setItem('unmanip_device_id', id); }
  return id;
}

// Kept for backward-compat; installed status no longer bypasses pricing.
export function isInstalled() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true
  );
}

export const api = axios.create({ baseURL: API, timeout: 180000 });

// Mobile browsers occasionally abort a request mid-flight (tab backgrounded, radio switch). Retry once.
api.interceptors.response.use(undefined, async (err) => {
  const cfg = err.config;
  const transient = (err.code === 'ERR_NETWORK' || /aborted|network error/i.test(err.message || '')) && !/timeout/i.test(err.message || '');
  if (cfg && !cfg.__retried && transient && !err.response) {
    cfg.__retried = true;
    return api.request(cfg);
  }
  throw err;
});

// Attach device id + waiver acceptance on every request
api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers['X-Device-Id'] = getDeviceId();
  const tok = localStorage.getItem('unmanip_token');
  if (tok) config.headers['Authorization'] = `Bearer ${tok}`;
  try {
    const w = JSON.parse(localStorage.getItem('unmanip_waiver_v1') || 'null');
    if (w?.version) config.headers['X-Waiver-Accepted'] = w.version;
  } catch {}
  if (localStorage.getItem('unmanip_deep') === '1') config.headers['X-Deep-Reasoning'] = '1';
  return config;
});

export const analyzeText = (payload) => api.post('/analyze/text', payload).then(r => r.data);
export const analyzeImage = (payload) => api.post('/analyze/image', payload).then(r => r.data);
export const analyzeLive = (payload) => api.post('/analyze/live', payload).then(r => r.data);
export const analyzeProfile = (payload) => api.post('/analyze/profile', payload).then(r => r.data);
export const getHistory = () => api.get('/history').then(r => r.data);
export const deleteHistory = (id) => api.delete(`/history/${id}`).then(r => r.data);

export const getCredits = () => api.get('/credits', { params: { device_id: getDeviceId() } }).then(r => r.data);
export const listPacks = () => api.get('/credits/packs').then(r => r.data);
export const packCheckout = (lookup_key) =>
  api.post('/credits/checkout', {
    lookup_key,
    origin_url: webOrigin(),
    device_id: getDeviceId(),
  }).then(r => r.data);
export const grantStartingCredits = (verdict) =>
  api.post('/credits/grant', { device_id: getDeviceId(), verdict }).then(r => r.data);

// Family · Special Features (parents only, quiet & free)
export const getFamilyStatus = () => api.get('/family/status', { params: { device_id: getDeviceId() } }).then(r => r.data);
export const familyScreen = (payload) => api.post('/family/screen', payload).then(r => r.data);
export const addFamilyKid = (kid_name, kid_age) => api.post('/family/add-kid', { kid_name, kid_age }).then(r => r.data);
export const analyzeFamily = (payload) => api.post('/analyze/family-scan', payload).then(r => r.data);

// Growth Path
export const getGrowthStatus = () => api.get('/growth/status', { params: { device_id: getDeviceId() } }).then(r => r.data);
export const growthStart = () => api.post('/growth/start', { device_id: getDeviceId() }).then(r => r.data);
export const growthReflect = (reflection) => api.post('/growth/reflect', { device_id: getDeviceId(), reflection }).then(r => r.data);

const MAX_EDGE = 1600;
const readDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Phone screenshots are 3–10 MB PNGs; mobile browsers abort huge uploads. Downscale + JPEG before sending.
export const fileToBase64 = async (file) => {
  const raw = await readDataUrl(file);
  if (!file.type?.startsWith('image/') || file.type === 'image/gif') return raw;
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = raw; });
    const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
    if (scale === 1 && file.size < 900_000) return raw;
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    const g = c.getContext('2d');
    g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
    g.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.86);
  } catch { return raw; }
};

/**
 * Safely extract a string message from an axios error.
 * Backend HTTPException(detail=...) can return either a string OR an object
 * (e.g. 402 insufficient_credits returns {error, balance, cost, unlock_pack, unlock_price_usd, message}).
 * Passing that object directly into toast/JSX crashes React.
 */
export function errMsg(e, fallback = 'Something went wrong') {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') {
    // Preserve most useful field
    return d.message || d.error || fallback;
  }
  if (e?.message && typeof e.message === 'string') return e.message;
  return fallback;
}
